import React, { useRef, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import testIcon from "@assets/icons/test-icon.svg";
import plusIcon from "@assets/icons/plus-icon.svg";
import searchIcon from "@assets/icons/search-icon.svg";
import resetIcon from "@assets/icons/reset-icon.svg";
import calendarIcon from "@assets/icons/calendar.svg";
import arrowDown from "@assets/icons/arrow-down.svg";
import chevronLeft from "@assets/icons/chevron-left.svg";
import chevronRight from "@assets/icons/chevron-right.svg";
import optionsIcons from "@assets/icons/options-icon.svg";
import { getExamSessionsByAdmin } from "@services/adminDashboard";

import styles from "./AdminDashboard.module.css";
import { subjectIDs } from "@constants/subject";
import Loader from "@components/loader/Loader";

function AdminDashboard() {
  const [testData, setTestData] = useState<any[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const calendarRef = useRef<HTMLInputElement>(null);

  const uniqueGrades = useMemo(() => {
    const grades = [...new Set(testData.map((test) => test.grade))].sort(
      (a, b) => a - b
    );
    return grades;
  }, [testData]);

  const uniqueSubjects = useMemo(() => {
    const subjects = [...new Set(testData.map((test) => test.subject))].sort();
    return subjects;
  }, [testData]);

  const filteredData = useMemo(() => {
    return testData.filter((test) => {
      const matchesSearch =
        searchTerm === "" ||
        test.testTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.testId.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesGrade =
        selectedGrade === "" || test.grade.toString() === selectedGrade;
      const matchesSubject =
        selectedSubject === "" || subjectIDs[test.subject] === selectedSubject;

      let matchesDate = true;
      if (selectedDate) {
        const selectedDateObj = new Date(selectedDate);
        const day = selectedDateObj.getDate();
        const month = selectedDateObj.getMonth() + 1;
        const year = selectedDateObj.getFullYear();
        const formattedSelectedDate = `${day}-${month}-${year}`;
        matchesDate = test.testDate === formattedSelectedDate;
      }

      return matchesSearch && matchesGrade && matchesSubject && matchesDate;
    });
  }, [testData, searchTerm, selectedGrade, selectedSubject, selectedDate]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGrade, selectedSubject, selectedDate, itemsPerPage]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleCalendarIconClick = () => {
    if (calendarRef.current) {
      if (calendarRef.current.showPicker) {
        calendarRef.current.showPicker();
      } else {
        calendarRef?.current?.click();
      }
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSelectedDate("");
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSelectedGrade("");
    setSelectedSubject("");
    setCurrentPage(1);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const navigateToExamPage = (examData: any) => {
    if (examData?.questionPaperIds?.length > 0) {
      navigate(
        `/exam-session?exam_id=${examData?.testId}&question_paper_id=${examData.questionPaperIds?.[0]}`
      );
    } else {
      navigate(`/create-test?exam_id=${examData?.testId}`);
    }
  };

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const fetchExamsCreatedByAdmin = async () => {
    setIsLoading(true);
    const res = await getExamSessionsByAdmin();
    if (res) {
      const testResp = res.map((item: any) => ({
        testId: item.id ?? "",
        testTitle: item.name ?? "",
        grade: item.grade_ids[0] ?? 0,
        subject: item.subject_ids[0] ?? "",
        createdOn: item.created_at ? formatDate(item.created_at) : "",
        testDate: item.exam_date ? formatDate(item.exam_date) : "",
        questionPaperIds: item?.question_paper_ids ?? [],
      }));
      setTestData(testResp);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("accessToken")) {
      fetchExamsCreatedByAdmin();
    }
  }, []);
  if (isLoading) return <Loader />;
  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.sideBar}>
        <ul>
          <li>
            {" "}
            <img src={testIcon} alt="Test Icon" /> Tests
          </li>
        </ul>
      </div>
      <div className={styles.mainContent}>
        <div className={styles.header}>
          <div className={styles.title}>
            <span>All Tests</span>
          </div>
          <div className={styles.testCreateButton}>
            <button onClick={() => navigate("/create-test")}>
              <img src={plusIcon} alt="Plus Icon" /> Create new test
            </button>
          </div>
        </div>
        <div className={styles.searchContainer}>
          <div className={styles.inputContainer}>
            <input
              type="text"
              placeholder="Search by Test Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img src={searchIcon} alt="Search Icon" />
          </div>
          <div className={styles.searchButtonContainer}>
            <button className={styles.searchButton} onClick={handleSearch}>
              Search
            </button>
            <button className={styles.clearButton} onClick={handleClearSearch}>
              <img src={resetIcon} alt="Reset Icon" />
            </button>
          </div>
          <div className={styles.calendarContainer}>
            <input
              type="date"
              ref={calendarRef}
              value={selectedDate}
              onChange={handleDateChange}
              style={{ visibility: "hidden" }}
            />
            <img
              src={calendarIcon}
              alt="Calendar Icon"
              className={styles.calendarIcon}
              onClick={handleCalendarIconClick}
            />
          </div>
        </div>
        <div className={styles.filters}>
          <div className={styles.filterItem}>
            <label>Grade</label>
            <div className={styles.selectContainer}>
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
              >
                <option value="">All</option>
                {uniqueGrades.map((grade) => (
                  <option key={grade} value={grade.toString()}>
                    {grade}
                  </option>
                ))}
              </select>
              <img
                src={arrowDown}
                alt="Arrow Down Icon"
                className={styles.arrowDownIcon}
              />
            </div>
          </div>
          <div className={styles.filterItem}>
            <label>Subject</label>
            <div className={styles.selectContainer}>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
              >
                <option value="">All</option>
                {uniqueSubjects.map((subject) => (
                  <option key={subject} value={subjectIDs[subject]}>
                    {subjectIDs[subject]}
                  </option>
                ))}
              </select>
              <img
                src={arrowDown}
                alt="Arrow Down Icon"
                className={styles.arrowDownIcon}
              />
            </div>
          </div>
          <div className={styles.filterItem}>
            <button className={styles.clearButton} onClick={handleClearFilters}>
              <img src={resetIcon} alt="Reset Icon" />
            </button>
          </div>
        </div>
        <div className={styles.testList}>
          <div className={styles.testListTitle}>
            <div className={styles.testListTitleText}>
              <span>All Tests</span>
            </div>
            <div className={styles.testListTitleActions}>
              <div className={styles.testListPageCountSelect}>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                </select>
                <img
                  src={arrowDown}
                  alt="Arrow Down Icon"
                  className={styles.arrowDownIcon}
                />
              </div>
              <div className={styles.testListPagination}>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={styles.testListPaginationButton}
                >
                  <img src={chevronLeft} alt="Previous" />
                </button>

                {totalPages > 0 && (
                  <>
                    <span
                      onClick={() => goToPage(1)}
                      className={`${styles.testListPaginationPageNumber} ${
                        currentPage === 1 ? styles.active : ""
                      }`}
                    >
                      1
                    </span>

                    {currentPage > 3 && (
                      <span className={styles.testListPaginationEllipsis}>
                        ...
                      </span>
                    )}

                    {currentPage > 2 && currentPage < totalPages && (
                      <span
                        onClick={() => goToPage(currentPage - 1)}
                        className={styles.testListPaginationPageNumber}
                        style={{ cursor: "pointer" }}
                      >
                        {currentPage - 1}
                      </span>
                    )}

                    {currentPage !== 1 &&
                      currentPage !== totalPages &&
                      totalPages > 1 && (
                        <span
                          className={`${styles.testListPaginationPageNumber} ${styles.active}`}
                        >
                          {currentPage}
                        </span>
                      )}

                    {currentPage < totalPages - 1 &&
                      currentPage !== totalPages && (
                        <span
                          className={styles.testListPaginationPageNumber}
                          onClick={() => goToPage(currentPage + 1)}
                          style={{ cursor: "pointer" }}
                        >
                          {currentPage + 1}
                        </span>
                      )}

                    {currentPage < totalPages - 2 && (
                      <span className={styles.testListPaginationEllipsis}>
                        ...
                      </span>
                    )}

                    {totalPages > 1 && (
                      <span
                        className={`${styles.testListPaginationPageNumber} ${
                          currentPage === totalPages ? styles.active : ""
                        }`}
                        onClick={() => goToPage(totalPages)}
                      >
                        {totalPages}
                      </span>
                    )}
                  </>
                )}

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className={styles.testListPaginationButton}
                >
                  <img src={chevronRight} alt="Next" />
                </button>
              </div>
            </div>
          </div>
          <div className={styles.testListTable}>
            <table>
              <thead>
                <tr>
                  <th>Test ID</th>
                  <th>Test Title</th>
                  <th>Grade</th>
                  <th>Subject</th>
                  <th>Create on</th>
                  <th>Test Date</th>
                  <th></th>
                </tr>
              </thead>
              {currentData.length > 0 && (
                <tbody>
                  {currentData.map((test: any) => (
                    <tr key={test.testId}>
                      <td>{test.testId}</td>
                      <td>{test.testTitle}</td>
                      <td>{test.grade}</td>
                      <td>{subjectIDs[test.subject]}</td>
                      <td>{test.createdOn}</td>
                      <td>{test.testDate}</td>
                      <td
                        className={styles.testActions}
                        onClick={() => navigateToExamPage(test)}
                      >
                        <img src={optionsIcons} alt="Options" />
                        <img
                          src={arrowDown}
                          className={styles.arrowRightIcon}
                          alt="Options"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
